import { Controller } from "@hotwired/stimulus"
import { range } from "d3"
import { parseDate, dayDiff } from "../../helpers/date_helpers"
import { RangeFieldsModel } from "../../configs/range_fields_model"
import microWait from "../../micro_wait"

class PresetHandler {
  constructor(data) {}
}

// Connects to data-controller="configs--form"
export default class extends Controller {
  static targets = [
    'mode', 'period', 'comparison', 'range', 'followCount', 'grouping',
    'groupingFinal', 'compareEnable', 'compareDisable', 'compareInfo',
    'compareInfoMsg'
  ]

  static values = {
    mode: { type: String, default: 'preset' },
    originalPeriod: { type: String, default: '' },
    grouping: { type: String, default: 'month' },
    presetData: { type: Object, default: {} },
    followCount: { type: Number, default: 0 },
  }

  static classes = ['custom', 'preset', 'comparison', 'multiRange', 'singleRange']

  connect() {
    app.cfgForm = this
    this.setGrouping(this.groupingValue)
  }

  periodTargetConnected() {
    // microWait gives TW Select one microtask tick to initialize before we read its value.
    // TODO: replace TW Select with a native select and remove this.
    microWait(() => {
      this.updateModel()
      this.toggleModeAppearance()
      this.handleFollowAreaAppearance()
    })
  }

  modeValueChanged() {
    this.modeTarget.value = this.modeValue
  }

  followCountValueChanged() {
    this.followCountTarget.value = this.followCountValue
  }

  change(event) {
    this.updateModel()
    if (!this.model.selectedPeriodRanges) return

    this.updateRangeDates()
    this.handleFollowAreaAppearance()
    this.selectGroupingRadioFor(this.model.suggestedUnit)
  }

  updateModel() {
    this.model = new RangeFieldsModel(this.formState)
  }

  updateRangeDates() {
    this.rangeTargets.forEach((t) => {
      const entry = this.model.selectedPeriodRanges[t.dataset.position]

      if (entry) {
        t.dataset["configs-RangeDateMinValue"] = entry.min
        t.dataset["configs-RangeDateMaxValue"] = entry.max
      }
    })
  }

  selectGroupingRadioFor(unit) {
    if (!unit) return

    this.setGrouping(unit)
  }

  /* Mode handling */
  /* ------------------------------------------------------------------------ */

  changeMode(event) {
    this.toggleMode()
    this.updateModel()
    this.toggleModeAppearance()
    this.dispatch("modeChange", { detail: { mode: this.modeValue } })
  }

  toggleMode() {
    if (this.modeValue == 'preset') {
      this.modeValue = 'custom'
      this.setPresetToCustom()
    } else {
      this.modeValue = 'preset'
      this.setPresetToOriginal()
    }
  }

  toggleModeAppearance() {
    if (this.modeValue == "custom") {
      this.element.classList.remove(...this.presetClasses)
      this.element.classList.add(...this.customClasses)
    } else {
      this.element.classList.remove(...this.customClasses)
      this.element.classList.add(...this.presetClasses)
    }
  }

  setPresetToCustom() {
    if (this.period != 'custom') this.originalPeriodValue = this.period
    this.periodTarget.value = 'custom'
  }

  setPresetToOriginal() {
    this.periodTarget.value = this.originalPeriodValue || 't12m'
  }

  get inCustomMode() { return this.modeValue == 'custom' }
  get inPresetMode() { return this.modeValue == 'preset' }

  /* ------------------------------------------------------------------------ */

  fieldChanged(event) {
    this.updateModel()
    this.handleFollowAreaAppearance()
  }

  // Sync grouping
  groupingChange(event) {
    this.setGrouping(event.target.value)
  }

  setGrouping(unit) {
    if (!unit) return

    this.groupingTargets.forEach((target) => {
      if (target.tagName === 'SELECT') {
        target.value = unit
      } else if (target.type === 'radio') {
        target.checked = target.value === unit
      }
    })

    this.groupingValue = unit

    if (this.hasGroupingFinalTarget) {
      this.groupingFinalTarget.value = unit
    }
  }

  /* Manage Follow Area Appearance */
  /* ------------------------------------------------------------------------ */

  enableFollowArea() {
    this.enableElem(this.compareDisableTarget)
    this.disableElem(this.compareEnableTarget)
    this.toggleCompareInfo()
  }

  disableFollowArea() {
    this.disableElem(this.compareDisableTarget)
    this.enableElem(this.compareEnableTarget)
    this.toggleCompareInfo()
  }

  // Status is completely dependent on state (not user)
  toggleCompareInfo() {
    if (this.model.shouldDisplayInfo) {
      this.enableElem(this.compareInfoTarget)
    } else {
      this.disableElem(this.compareInfoTarget)
    }
  }

  handleFollowAreaAppearance() {
    this.toggleCompareArea()
    this.toggleYearOption()
    this.toggleAddComparison()
    this.toggleCompareInfo()
  }

  toggleYearOption() {
    if (this.model.shouldEnableYearOption) {
      this.yearOption.removeAttribute('disabled')
    } else {
      this.priorComparison = this.comparison
      this.comparison = 'period'
      this.yearOption.setAttribute('disabled', 'disabled')
    }
  }

  get yearOption() {
    return this.comparisonTargets.find((t) => t.value == "year")
  }

  toggleCompareArea() {
    if (this.model.shouldDisplayComparisons) {
      this.enableElem(this.compareDisableTarget)
      this.disableElem(this.compareEnableTarget)
    } else {
      this.disableElem(this.compareDisableTarget)
      this.enableElem(this.compareEnableTarget)
    }
  }

  toggleAddComparison() {
    if (this.model.shouldDisplayAddComparison) {
      this.enableElem(this.compareEnableTarget)
    } else {
      this.disableElem(this.compareEnableTarget)
    }
  }

  get comparison() {
    return this.comparisonTargets.find((target) => target.checked)?.value
  }

  set comparison(val) {
    this.comparisonTargets.forEach((t) => {
      if (val == t.value) {
        t.checked = true
      } else {
        t.checked = false
      }
    })
    this.comparisonValue = val
  }

  /* ------------------------------------------------------------------------ */

  // Selected period
  get period() {
    return this.periodTarget.value
  }

  /* Preset Data */
  /* ------------------------------------------------------------------------ */

  // get selectedPeriodData() {
  //   return this.presetDataValue?.map?.[this.period]
  // }

  /* ------------------------------------------------------------------------ */

  /* Read Leader Range */
  /* ------------------------------------------------------------------------ */

  get leaderRange() {
    return this.rangeTargets.find((r) => r.dataset.role === 'leader')
  }

  get leaderDateMin() {
    return parseDate(this.leaderRange.dataset['configs-RangeDateMinValue'])
  }

  get leaderDateMax() {
    return parseDate(this.leaderRange.dataset['configs-RangeDateMaxValue'])
  }

  get leaderDateRange() {
    return [this.leaderDateMin, this.leaderDateMax]
  }

  // get leaderRangeDuration() {
  //   if (this.leaderDateRange.every((dt) => dt.isValid)) {
  //     return dayDiff(this.leaderDateMin, this.leaderDateMax)
  //   }
  // }

  /* ------------------------------------------------------------------------ */

  // Private-ish

  get formState() {
    return {
      mode: this.modeValue,
      period: this.period,
      comparison: this.comparison,
      presetData: this.presetDataValue,
      grouping: this.groupingValue,
      followCount: this.followCountValue,
      leaderRange: this.leaderDateRange
    }
  }

  enableElem(elem) {
    const enabledClasses = elem.dataset.enabled.split(' ')
    const disabledClasses = elem.dataset.disabled.split(' ')
    elem.classList.remove(...disabledClasses)
    elem.classList.add(...enabledClasses)
  }

  disableElem(elem) {
    const enabledClasses = elem.dataset.enabled.split(' ')
    const disabledClasses = elem.dataset.disabled.split(' ')
    elem.classList.remove(...enabledClasses)
    elem.classList.add(...disabledClasses)
  }
}
